import { useAuth0V2 } from "@stadium/services/auth0";
import { useCallback, useContext, useMemo } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useWithAnalytics } from "@stadium/services/analytics/hooks/use-with-analytics";

import PersonalWorkspaceNudge from "@stadium/shared/src/components/personal-workspace-nudge";
import { PersonalWorkspaceNudgeVariant } from "@stadium/shared/src/constants/personal-workspace-nudge";

import { useSelector } from "react-redux";

import { NewCountrySelector } from "@stadium/ui/src/components/country-selector";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { showSuccessToast } from "@stadium/services/react-toastify";

import { getUsCountryId, getCurrencies } from "@stadium/redux-utils/selectors/configSelector";
import useAllCountries from "@hooks/countries/use-all-countries";
import useFetchExternalStore from "@hooks/use-fetch-external-store";

import { useConfig } from "@stadium/hooks/lib/use-config";

import { getCountry } from "@selectors/country";
import { getCartOrderId, getReceivedItemsCount } from "@selectors/cart";
import {
  getIsHideCountryFlag,
  getIsKitStore,
  getIsLockerShop,
  getIsPricelessShop,
  getIsShopPrivate,
  getStoreNumber,
} from "@selectors/store";
import { getBookACallCtaText } from "@stadium/shared/src/selectors/book-a-call";

import { getStorePreviewModeCartKey } from "@helpers/store";

import { updateOrderDetails } from "@actions/order";

import UserPointsBalance from "@components/store/navigation/points-balance";
import DropdownToggleMenu from "@components/store/navigation/ProfileDropdown";
import UnAuthtenticatedRightSection from "@components/store/navigation/unauthenticated-right-section";
import AuthLogout from "@components/auth/buttons/logout";
import SendItemsSelection from "@components/store/navigation/send-items-selection";
import SelectedItemsCount from "@components/store/navigation/selected-items-count";
import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import CheckoutProvider from "@contexts/checkout-context";
import { useWorkspaceContext } from "@contexts/workspace-context";
import { WeglotContext } from "@stadium/services/weglot/provider";
import { ExternalButton } from "@sharedComponents/buttons";

import { useAppDispatch } from "@hooks/redux-store";
import { useWindowStorage } from "@hooks/use-window-storage";
import useEmptyCart from "@hooks/cart/use-empty-cart";
import useCountryChange from "@hooks/use-country-change";
import { useLanguageChange } from "@hooks/use-language-change";
import { useStoreContext } from "@components/store/provider";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useCreateShopContext } from "@contexts/create-shop-context";

import { isGlobalCountry } from "@stadium/helpers/country/utils";

import { SM_PROGRAM_ID } from "@constants/app-defaults";

import { CATALOG_REQUEST_ITEM_TYPE_FORM_LINK } from "@stadium/shared/src/constants/stores/routes";
import {
  BOOK_A_CALL,
  STORE_HOME_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME,
  MENU_PAGE_PATHNAME_OLD,
  PRODUCT_VIEW_PATHNAME,
  CART_PAGE_PATHNAME,
} from "@constants/routes";
import {
  OLD_STORE_HOME_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME,
  OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
  OLD_PRODUCT_VIEW_PATHNAME,
  OLD_CART_PAGE_PATHNAME,
} from "@constants/old-routes";
import { ANALYTICS_EVENT_ACTION_CHANGED } from "@stadium/services/analytics/constants/default";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";

import { BYSTADIUM_MARKETING_URL } from "@stadium/helpers/src/app-routes";
import { EMPTY_CART_SUCCESS_TOAST_ID } from "@constants/toast-ids";
import { emptyCart as emptyOrderCart } from "@slices/cart";

import styles from "./main.module.scss";

export const CountrySelector = ({ onSelect = null }) => {
  const router = useRouter();
  const { pathname: routerPathName } = router;

  const dispatch = useAppDispatch();

  const { isAuthenticated } = useAuth0V2();

  const isKitStore = useSelector(getIsKitStore);
  const selectedCountry = useSelector(getCountry);
  const orderId = useSelector(getCartOrderId);
  const currencies = useConfig(getCurrencies);
  const usCountryId = useConfig(getUsCountryId);
  const storeNumber = useSelector(getStoreNumber);
  const hideCountryFlag = useSelector(getIsHideCountryFlag);

  const { instance: Weglot, availableLanguages } = useContext(WeglotContext);
  const {
    showCurrencySelector,
    displayCurrency,
    setDisplayCurrency,
    getUpdatedStoreBasePath,
    countryCode,
  } = useStoreContext();
  const { isSendToMyself } = useSendItemsContext();
  const { emptyCart } = useEmptyCart();

  const { allCountriesList } = useAllCountries({
    includeGlobalCountry: !isSendToMyself,
  });

  const { changeCountry } = useCountryChange(allCountriesList);
  const { changeLang, langInUrl } = useLanguageChange();
  const { isNewShopCreationFlow } = useCreateShopContext();
  const withAnalytics = useWithAnalytics();
  const [, , clearPreviewModeCart] = useWindowStorage(
    getStorePreviewModeCartKey(storeNumber, countryCode, isSendToMyself),
  );

  const showCountryChangeModal = !(isKitStore || isNewShopCreationFlow || !isSendToMyself);

  const isCartPage = [CART_PAGE_PATHNAME, OLD_CART_PAGE_PATHNAME].includes(routerPathName);

  const redirectToMenuRoute = (country) => {
    router.replace(
      getUpdatedStoreBasePath({
        countryCode: country.iso.toLowerCase(),
      }),
    );
  };

  const updateOrderCountry = async (country) => {
    if (!isAuthenticated) {
      clearPreviewModeCart();
      dispatch(emptyOrderCart());
      showSuccessToast({
        message: "All the items have been removed from your bag.",
        config: { toastId: EMPTY_CART_SUCCESS_TOAST_ID },
      });
      return;
    }
    await dispatch(
      updateOrderDetails(orderId, { program_id: SM_PROGRAM_ID, country_id: country.id }),
    );
    await emptyCart(orderId);
  };

  const onSelectCountry = useCallback(
    async (country) => {
      withAnalytics(undefined, {
        track: true,
        target: HEADER_ANALYTICS_TARGET.COUNTRY,
        action: ANALYTICS_EVENT_ACTION_CHANGED,
        eventProperties: { country_code: country?.iso?.toLowerCase() },
      })();
      if (orderId && isSendToMyself && !isNewShopCreationFlow) {
        const isGlobal = isGlobalCountry(country);
        const countryId = isGlobal ? usCountryId : country.id;
        await updateOrderCountry({ ...country, id: countryId });
      }
      if (isCartPage) {
        return redirectToMenuRoute(country);
      }
      changeCountry(country);
    },
    [changeCountry, isCartPage, isNewShopCreationFlow, isSendToMyself, orderId, updateOrderCountry],
  );

  const currencySelectorProps = useMemo(() => {
    return {
      setCurrency: setDisplayCurrency,
      currency: displayCurrency,
    };
  }, [setDisplayCurrency, displayCurrency]);

  return (
    <NewCountrySelector
      currencies={showCurrencySelector ? currencies : []}
      currencySelectorProps={currencySelectorProps}
      countries={allCountriesList}
      onSelectCountry={onSelect ?? onSelectCountry}
      selectedCountry={selectedCountry}
      flagWidth={32}
      flagHeight={24}
      weglotInstance={Weglot}
      availableLanguages={availableLanguages}
      onSelectLang={changeLang}
      langInUrl={langInUrl}
      type="round"
      showCountryChangeModal={showCountryChangeModal}
      arrowCustomClass={styles.iconArrow}
      hideCountryFlag={hideCountryFlag}
    />
  );
};

const NavigationRightSection = ({ expanded }) => {
  const router = useRouter();
  const { pathname: routerPathName } = router;

  const { isPersonalWorkspace } = useWorkspaceContext();
  const { showRewardPoints } = useStoreContext();

  const { isAuthenticated } = useAuth0V2();

  const isLockerShop = useSelector(getIsLockerShop);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);
  const bookACallCtaText = useConfig(getBookACallCtaText);
  const isShopPrivate = useSelector(getIsShopPrivate);
  const isPrivateShopAndNotLockerShop = isShopPrivate && !isLockerShop;
  const { showSendToManyOption } = useSendItemsContext();

  const { isNewShopCreationFlow } = useCreateShopContext();

  const shouldShowSendItemIcon = [
    STORE_HOME_PAGE_PATHNAME,
    MENU_PAGE_PATHNAME,
    MENU_PAGE_PATHNAME_OLD,
    PRODUCT_VIEW_PATHNAME,
    OLD_STORE_HOME_PAGE_PATHNAME,
    OLD_MENU_PAGE_PATHNAME,
    OLD_MENU_PAGE_PATHNAME_WITH_SUBCATEGORY,
    OLD_PRODUCT_VIEW_PATHNAME,
  ].includes(routerPathName);

  const { isMobileDevice, isDesktopDevice, isTabletDevice } = useContext(CurrentDeviceContext);

  const { loading } = useFetchExternalStore();

  const renderProfileSection = () => {
    if (isNewShopCreationFlow) return null;
    if (isAuthenticated)
      return (
        <div className={styles.personalWorkspaceNudgeProfileWrap}>
          <PersonalWorkspaceNudge
            variant={
              isMobileDevice || isTabletDevice
                ? PersonalWorkspaceNudgeVariant.RESPONSIVE
                : PersonalWorkspaceNudgeVariant.WEB
            }
            show={isPersonalWorkspace}
          />
          <DropdownToggleMenu id="profile" dropDownParentClass={styles.userProfileblock} />
        </div>
      );
    return <UnAuthtenticatedRightSection />;
  };

  const renderNavigationRightSection = () => (
    <div className={styles.dropdownBlock}>
      <CountrySelector />
      {!(isMobileDevice || isTabletDevice) && showSendToManyOption && shouldShowSendItemIcon && (
        <SendItemsSelection />
      )}
      {
        <>
          {isPricelessShop &&
            receivedItemsCount > 0 &&
            (loading ? (
              <Spinner />
            ) : (
              <CheckoutProvider skipPaymentOptions>
                <SelectedItemsCount />
              </CheckoutProvider>
            ))}
          {showRewardPoints && !isPricelessShop && !(isMobileDevice || isTabletDevice) && (
            <CheckoutProvider skipPaymentOptions>
              <UserPointsBalance />
            </CheckoutProvider>
          )}
        </>
      }
      {renderProfileSection()}
      {isLockerShop && (
        <div className={styles.swagExpertContainerMobile}>
          <ExternalButton
            href={CATALOG_REQUEST_ITEM_TYPE_FORM_LINK}
            styleType="primary-outline"
            className={styles.requestLink}
            target="_blank"
          >
            Request Expert Swag Help
          </ExternalButton>
          <ExternalButton
            href={BOOK_A_CALL}
            styleType="primary"
            className={styles.swagExpertButton}
          >
            {bookACallCtaText}
          </ExternalButton>
        </div>
      )}
      {showRewardPoints && !isDesktopDevice && !isPricelessShop && (
        <div
          className={`${styles.colAuto} ${styles.pr0} ${styles.mbFullWidth} ${styles.pointBalanceMobile} `}
        >
          <CheckoutProvider skipPaymentOptions>
            <UserPointsBalance />
          </CheckoutProvider>
        </div>
      )}
      {!isDesktopDevice && isAuthenticated && (
        <AuthLogout
          logoutUrl={isPrivateShopAndNotLockerShop ? BYSTADIUM_MARKETING_URL : ""}
          className={styles.logoutButton}
        >
          <div className={`${styles.loginText}`}>LOGOUT</div>
        </AuthLogout>
      )}
      {(isMobileDevice || isTabletDevice) && showSendToManyOption && shouldShowSendItemIcon && (
        <SendItemsSelection />
      )}
    </div>
  );

  return isMobileDevice && expanded ? (
    <BottomIconHideWrapper>{renderNavigationRightSection()}</BottomIconHideWrapper>
  ) : (
    renderNavigationRightSection()
  );
};

export default NavigationRightSection;
